import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { FuseTranslationLoaderService } from '../../../../../../@fuse/services/translation-loader.service';
import { GT2PageAbstract } from '../../../abstract/GT2PageAbstract';
import { AutomationService } from '../../../../api/automation.service';
import { AutomationPanelService } from '../../../../services/automation-panel.service';
import { locale as english } from '../i18n/en-CA';
import { locale as french } from '../i18n/fr-CA';

/**
 * Create/edit right-side panel structured after the data model: Quoi (task
 * type + sub-form) / Quand (freq presets → RRULE composed here) / Paramètre
 * (date range preset) / Action (emails). Ends with a live human-readable
 * summary. Data comes from AutomationPanelService.getData(); the result is
 * emitted back through emitResult() (same pattern as the other side panels).
 */
@Component({
  selector: 'app-automation-dialog',
  templateUrl: './automation-dialog.component.html',
  styleUrls: ['./automation-dialog.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatInputModule,
    CommonModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
  ],
})
export class AutomationDialogComponent
  extends GT2PageAbstract
  implements OnInit
{
  static CREATE: string = 'CREATE';
  static EDIT: string = 'EDIT';

  static WEEK_DAYS: string[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

  mode: string = AutomationDialogComponent.CREATE;
  form: UntypedFormGroup;
  catalog: any = null;
  automationToUpdate: any = null;
  override loading: boolean = false;

  weekDays: string[] = AutomationDialogComponent.WEEK_DAYS;
  selectedDays: { [day: string]: boolean } = {};

  constructor(
    private panelService: AutomationPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    public automationService: AutomationService,
    public dialog: MatDialog,
    public router: Router,
    private toastr: ToastrService,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();

    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(191)]],
      trigger_type: ['SCHEDULE', [Validators.required]],
      trigger_event: [''],
      task_type: ['REPORT', [Validators.required]],
      report_key: ['', [Validators.required]],
      followup_step_1: [3],
      followup_step_2: [7],
      followup_step_3: [14],
      notify_internal: [true],
      notify_responsible_employee: [false],
      paper: ['letter', [Validators.required]],
      orientation: ['portrait', [Validators.required]],
      locale: ['fr-CA', [Validators.required]],
      freq: ['DAILY', [Validators.required]],
      by_month_day: [1],
      run_time: ['19:00', [Validators.required]],
      date_range_preset: ['TOMORROW', [Validators.required]],
      date_range_days: [7],
      action_type: ['EMAIL', [Validators.required]],
      email_to: ['', [Validators.required]],
      email_cc: [''],
      email_bcc: [''],
      email_subject: ['', [Validators.maxLength(191)]],
      webhook_url: [''],
      webhook_secret: [''],
      overwrite: [false],
      is_active: [true],
    });

    // Déclencheur : SCHEDULE (rapport planifié) ou EVENT (notification sur événement).
    // Les validateurs du planning ne s'appliquent qu'au mode SCHEDULE.
    this.form
      .get('trigger_type')
      ?.valueChanges.subscribe((type: string) =>
        this.applyTriggerValidators(type),
      );

    // Tâche planifiée : REPORT (rapport) ou PROPOSAL_FOLLOWUP (relance des propositions).
    this.form
      .get('task_type')
      ?.valueChanges.subscribe(() =>
        this.applyTriggerValidators(this.form.value.trigger_type),
      );

    // Recipients are only required for the EMAIL action.
    this.form.get('action_type')?.valueChanges.subscribe((type: string) => {
      const emailTo = this.form.get('email_to');
      const webhookUrl = this.form.get('webhook_url');
      if (emailTo) {
        if (type === 'EMAIL') {
          emailTo.setValidators([Validators.required]);
        } else {
          emailTo.clearValidators();
        }
        emailTo.updateValueAndValidity();
      }
      if (webhookUrl) {
        if (type === 'WEBHOOK') {
          webhookUrl.setValidators([
            Validators.required,
            Validators.pattern(/^https:\/\/\S+$/i),
          ]);
        } else {
          webhookUrl.clearValidators();
        }
        webhookUrl.updateValueAndValidity();
      }
    });

    if (data && data.mode) {
      this.mode = data.mode;
    }
    if (data && data.catalog) {
      this.catalog = data.catalog;
    }
    if (data && data.automationToUpdate) {
      this.automationToUpdate = data.automationToUpdate;
      this.fillFormFromRow(data.automationToUpdate);
    }
    this.applyTriggerValidators(this.form.value.trigger_type);
  }

  /**
   * SCHEDULE + REPORT → report/time/period required; SCHEDULE + PROPOSAL_FOLLOWUP →
   * time + first step required, no period (the task is not date-driven);
   * EVENT → event required, schedule fields free.
   */
  private applyTriggerValidators(type: string): void {
    const isEvent = type === 'EVENT';
    const isFollowup = !isEvent && this.isFollowupTask;
    const isReport = !isEvent && !isFollowup;
    const required = (name: string, on: boolean, extra: any[] = []) => {
      const control = this.form.get(name);
      if (!control) {
        return;
      }
      control.setValidators(on ? [Validators.required, ...extra] : []);
      control.updateValueAndValidity({ emitEvent: false });
    };
    required('trigger_event', isEvent);
    required('report_key', isReport);
    required('paper', isReport);
    required('orientation', isReport);
    required('freq', !isEvent);
    required('run_time', !isEvent);
    required('date_range_preset', isReport && this.taskNeedsDateRange);
    required('followup_step_1', isFollowup, [
      Validators.min(1),
      Validators.max(60),
    ]);
    // Un EVENT ne peut pas livrer vers un Drive : on ramène sur Courriel.
    if (isEvent && this.isDriveAction) {
      this.form.patchValue({ action_type: 'EMAIL' });
    }
    // Une relance de proposition ne se dépose pas dans un Drive ni un webhook.
    if (isFollowup && this.form.value.action_type !== 'EMAIL') {
      this.form.patchValue({ action_type: 'EMAIL' });
    }
  }

  public get isFollowupTask(): boolean {
    return this.form.value.task_type === 'PROPOSAL_FOLLOWUP';
  }

  /** Scheduled task types the catalog offers (GOD_ONLY ones already filtered by the API). */
  public get scheduledTaskTypes(): any[] {
    return (this.catalog?.task_types || []).filter(
      (taskType: any) =>
        !Array.isArray(taskType.triggers) ||
        taskType.triggers.indexOf('SCHEDULE') >= 0,
    );
  }

  public get hasTaskChoice(): boolean {
    return this.scheduledTaskTypes.length > 1;
  }

  /** Catalog `date_range: false` = the « Paramètre » block is hidden (defaults to true). */
  public get taskNeedsDateRange(): boolean {
    const taskType = (this.catalog?.task_types || []).find(
      (item: any) => item.task_type === this.form.value.task_type,
    );
    return taskType ? taskType.date_range !== false : true;
  }

  /** Follow-up steps as entered (blank steps 2/3 dropped), in order. */
  public get followupSteps(): number[] {
    const value = this.form.value;
    return [value.followup_step_1, value.followup_step_2, value.followup_step_3]
      .map((step: any) =>
        step === null || step === undefined || step === ''
          ? null
          : Number(step),
      )
      .filter((step: number | null): step is number => step !== null);
  }

  /** AUT0007 mirror: steps must be strictly increasing day counts between 1 and 60. */
  public get followupStepsInvalid(): boolean {
    if (!this.isFollowupTask) {
      return false;
    }
    const steps = this.followupSteps;
    if (steps.length === 0) {
      return true;
    }
    let previous = 0;
    for (const step of steps) {
      if (!Number.isInteger(step) || step <= previous || step > 60) {
        return true;
      }
      previous = step;
    }
    return false;
  }

  public get followupStepsLabel(): string {
    return this.followupSteps.map((step) => 'J+' + step).join(', ');
  }

  public get isEventTrigger(): boolean {
    return this.form.value.trigger_type === 'EVENT';
  }

  /** Events offered by the catalog (module + GOD_ONLY already filtered by the API). */
  public get triggerEvents(): any[] {
    return this.catalog?.trigger_events || [];
  }

  public eventLabel(item: any): string {
    return (
      item?.label?.[this.translate.currentLang] ||
      item?.label?.['fr-CA'] ||
      item?.event ||
      ''
    );
  }

  public get isWebhookAction(): boolean {
    return this.form.value.action_type === 'WEBHOOK';
  }

  /** Secret already stored server-side (never echoed back) — blank input keeps it. */
  public get hasStoredSecret(): boolean {
    return !!this.automationToUpdate?.action_config?.has_secret;
  }

  ngOnInit(): void {}

  public close(): void {
    this.panelService.emitResult(false);
  }

  /** Reports offered by the catalog for the REPORT task type. */
  public get reports(): any[] {
    const reportType = (this.catalog?.task_types || []).find(
      (taskType: any) => taskType.task_type === 'REPORT',
    );
    return reportType?.reports || [];
  }

  /**
   * Action types the catalog offers (GOD_ONLY ones are already filtered by the API).
   * EVENT trigger (NOTIFICATION task) → EMAIL + WEBHOOK only; SCHEDULE (REPORT) → no WEBHOOK.
   */
  public get actionTypes(): string[] {
    const types: string[] = this.catalog?.action_types || [];
    const all = types.length > 0 ? types : ['EMAIL'];
    if (this.isEventTrigger) {
      const allowed = all.filter(
        (type) => type === 'EMAIL' || type === 'WEBHOOK',
      );
      return allowed.length > 0 ? allowed : ['EMAIL'];
    }
    if (this.isFollowupTask) {
      return ['EMAIL'];
    }
    return all.filter((type) => type !== 'WEBHOOK');
  }

  // GOOGLE_DRIVE and ONEDRIVE share the same form (no recipients, an "overwrite" flag).
  public get isDriveAction(): boolean {
    return (
      this.form.value.action_type === 'GOOGLE_DRIVE' ||
      this.form.value.action_type === 'ONEDRIVE'
    );
  }

  public get isOneDriveAction(): boolean {
    return this.form.value.action_type === 'ONEDRIVE';
  }

  public reportLabel(report: any): string {
    return (
      report.label?.[this.translate.currentLang] ||
      report.label?.['fr-CA'] ||
      report.report_key
    );
  }

  public toggleDay(day: string): void {
    this.selectedDays[day] = !this.selectedDays[day];
  }

  private fillFormFromRow(row: any): void {
    this.form.patchValue({
      name: row.name,
      trigger_type: row.trigger_type || 'SCHEDULE',
      trigger_event: row.trigger_config?.event || '',
      task_type:
        row.task_type === 'NOTIFICATION' ? 'REPORT' : row.task_type || 'REPORT',
      report_key: row.task_config?.report_key || '',
      followup_step_1: (row.task_config?.steps || [3])[0] ?? null,
      followup_step_2: (row.task_config?.steps || [3, 7])[1] ?? null,
      followup_step_3: (row.task_config?.steps || [3, 7, 14])[2] ?? null,
      notify_internal: row.task_config?.notify_internal !== false,
      notify_responsible_employee:
        !!row.task_config?.notify_responsible_employee,
      paper: row.task_config?.paper || 'letter',
      orientation: row.task_config?.orientation || 'portrait',
      locale: row.task_config?.locale || 'fr-CA',
      freq: row.schedule?.freq || 'DAILY',
      by_month_day: (row.schedule?.by_month_day || [])[0] || 1,
      run_time: row.run_time || '19:00',
      date_range_preset: row.date_range_preset || 'TOMORROW',
      date_range_days: row.date_range_days || 7,
      action_type: row.action_type || 'EMAIL',
      email_to: (row.action_config?.to || []).join(', '),
      email_cc: (row.action_config?.cc || []).join(', '),
      email_bcc: (row.action_config?.bcc || []).join(', '),
      email_subject: row.task_config?.subject || '',
      webhook_url: row.action_config?.url || '',
      webhook_secret: '',
      overwrite: !!row.action_config?.overwrite,
      is_active: row.is_active,
    });
    for (const day of row.schedule?.by_day || []) {
      this.selectedDays[day] = true;
    }
  }

  /** FREQ presets → RRULE string (the user never sees RRULE syntax). */
  public composeRrule(): string {
    const freq = this.form.value.freq;
    if (freq === 'WEEKLY') {
      const days = this.weekDays.filter((day) => this.selectedDays[day]);
      return 'FREQ=WEEKLY;BYDAY=' + days.join(',');
    }
    if (freq === 'MONTHLY') {
      return 'FREQ=MONTHLY;BYMONTHDAY=' + (this.form.value.by_month_day || 1);
    }
    return 'FREQ=DAILY';
  }

  private splitEmails(raw: string): string[] {
    return (raw || '')
      .split(/[,;]/)
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);
  }

  public get weeklyMissingDays(): boolean {
    return (
      !this.isEventTrigger &&
      this.form.value.freq === 'WEEKLY' &&
      !this.weekDays.some((day) => this.selectedDays[day])
    );
  }

  /** Live human-readable summary — the main guard against misconfiguration. */
  public get summary(): string {
    const value = this.form.value;
    if (this.isEventTrigger) {
      const eventItem = this.triggerEvents.find(
        (item: any) => item.event === value.trigger_event,
      );
      const event = eventItem ? this.eventLabel(eventItem) : '';
      if (value.action_type === 'WEBHOOK') {
        return this.translate.instant('AUTOMATIONS.SUMMARY_EVENT_WEBHOOK', {
          event,
          host: this.hostOf(value.webhook_url),
        });
      }
      return this.translate.instant('AUTOMATIONS.SUMMARY_EVENT', {
        event,
        recipients: this.splitEmails(value.email_to).length,
      });
    }
    const time = value.run_time || '';
    let when = '';
    if (value.freq === 'DAILY') {
      when = this.translate.instant('AUTOMATIONS.WHEN_DAILY', { time });
    } else if (value.freq === 'WEEKLY') {
      const days = this.weekDays
        .filter((day) => this.selectedDays[day])
        .map((day) => this.translate.instant('AUTOMATIONS.DAY_' + day))
        .join(', ');
      when = this.translate.instant('AUTOMATIONS.WHEN_WEEKLY', { days, time });
    } else {
      when = this.translate.instant('AUTOMATIONS.WHEN_MONTHLY', {
        day: value.by_month_day || 1,
        time,
      });
    }

    if (this.isFollowupTask) {
      return this.translate.instant('AUTOMATIONS.SUMMARY_FOLLOWUP', {
        when,
        steps: this.followupStepsLabel,
        recipients: value.notify_internal
          ? this.splitEmails(value.email_to).length
          : 0,
      });
    }

    const report = this.reports.find(
      (item: any) => item.report_key === value.report_key,
    );
    const reportName = report ? this.reportLabel(report) : '';
    const param =
      value.date_range_preset === 'NEXT_N_DAYS'
        ? this.translate.instant('AUTOMATIONS.PRESET_NEXT_N_DAYS', {
            n: value.date_range_days,
          })
        : this.translate.instant(
            'AUTOMATIONS.PRESET_' + value.date_range_preset,
          );
    if (value.action_type === 'GOOGLE_DRIVE') {
      return this.translate.instant('AUTOMATIONS.SUMMARY_DRIVE', {
        when,
        report: reportName,
        param: param.toLowerCase(),
      });
    }
    if (value.action_type === 'ONEDRIVE') {
      return this.translate.instant('AUTOMATIONS.SUMMARY_ONEDRIVE', {
        when,
        report: reportName,
        param: param.toLowerCase(),
      });
    }

    const recipients = this.splitEmails(value.email_to).length;

    return this.translate.instant('AUTOMATIONS.SUMMARY', {
      when,
      report: reportName,
      param: param.toLowerCase(),
      recipients,
    });
  }

  private hostOf(url: string): string {
    try {
      return new URL(url || '').host;
    } catch (e) {
      return url || '';
    }
  }

  private buildEventPayload(value: any): any {
    const taskConfig: any = { locale: value.locale };
    if ((value.email_subject || '').trim() !== '') {
      taskConfig.subject = value.email_subject.trim();
    }
    let actionConfig: any;
    if (value.action_type === 'WEBHOOK') {
      actionConfig = { url: (value.webhook_url || '').trim() };
      // Blank secret on edit = keep the stored one (the API never echoes it back).
      if ((value.webhook_secret || '').trim() !== '') {
        actionConfig.secret = value.webhook_secret.trim();
      }
    } else {
      actionConfig = {
        to: this.splitEmails(value.email_to),
        cc: this.splitEmails(value.email_cc),
        bcc: this.splitEmails(value.email_bcc),
      };
    }
    return {
      name: value.name,
      trigger_type: 'EVENT',
      trigger_config: { event: value.trigger_event },
      task_type: 'NOTIFICATION',
      task_config: taskConfig,
      rrule: null,
      run_time: null,
      date_range_preset: null,
      date_range_days: null,
      action_type: value.action_type || 'EMAIL',
      action_config: actionConfig,
      is_active: value.is_active,
    };
  }

  public buildPayload(): any {
    const value = this.form.value;
    if (this.isEventTrigger) {
      const eventPayload = this.buildEventPayload(value);
      if (this.automationToUpdate?.availability) {
        eventPayload.availability = this.automationToUpdate.availability;
      }
      return eventPayload;
    }
    if (this.isFollowupTask) {
      const followupPayload: any = {
        name: value.name,
        trigger_type: 'SCHEDULE',
        task_type: 'PROPOSAL_FOLLOWUP',
        task_config: {
          steps: this.followupSteps,
          locale: value.locale,
          notify_internal: !!value.notify_internal,
          notify_responsible_employee: !!value.notify_responsible_employee,
        },
        rrule: this.composeRrule(),
        run_time: value.run_time,
        date_range_preset: null,
        date_range_days: null,
        action_type: 'EMAIL',
        action_config: {
          to: this.splitEmails(value.email_to),
          cc: this.splitEmails(value.email_cc),
          bcc: this.splitEmails(value.email_bcc),
        },
        is_active: value.is_active,
      };
      if (this.automationToUpdate?.availability) {
        followupPayload.availability = this.automationToUpdate.availability;
      }
      return followupPayload;
    }
    const payload: any = {
      name: value.name,
      trigger_type: 'SCHEDULE',
      task_type: 'REPORT',
      task_config: {
        report_key: value.report_key,
        paper: value.paper,
        orientation: value.orientation,
        locale: value.locale,
      },
      rrule: this.composeRrule(),
      run_time: value.run_time,
      date_range_preset: value.date_range_preset,
      date_range_days:
        value.date_range_preset === 'NEXT_N_DAYS'
          ? value.date_range_days
          : null,
      action_type: value.action_type || 'EMAIL',
      action_config:
        value.action_type === 'GOOGLE_DRIVE' || value.action_type === 'ONEDRIVE'
          ? { overwrite: !!value.overwrite }
          : {
              to: this.splitEmails(value.email_to),
              cc: this.splitEmails(value.email_cc),
              bcc: this.splitEmails(value.email_bcc),
            },
      is_active: value.is_active,
    };
    if (this.automationToUpdate?.availability) {
      payload.availability = this.automationToUpdate.availability;
    }
    return payload;
  }

  public onSave(): void {
    if (
      this.form.invalid ||
      (!this.isEventTrigger && this.weeklyMissingDays) ||
      this.followupStepsInvalid
    ) {
      return;
    }
    this.loading = true;
    const payload = this.buildPayload();

    const request =
      this.mode === AutomationDialogComponent.EDIT
        ? this.automationService.updateAutomation(
            this.automationToUpdate.uuid,
            payload,
          )
        : this.automationService.createAutomation(payload);

    request.subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success(
          '',
          this.translate.instant(
            this.mode === AutomationDialogComponent.EDIT
              ? 'AUTOMATIONS.EDIT_SUCCESS'
              : 'AUTOMATIONS.CREATE_SUCCESS',
          ),
        );
        this.panelService.emitResult(true);
      },
      error: (error: any) => {
        this.loading = false;
        this.logger.error(
          'AutomationDialogComponent.onSave() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(
          error,
          this.dialog,
          this.toastr,
          this.translate.instant('GENERIC.ERRORS.GENERIC') + ' AUT005',
          this.router,
        );
      },
    });
  }
}
